/**
 * * Project Name : eco presence
 * * Component Name : FaqView
 * * Description : Modern, interactive FAQ display component with category-wise grouping
 * * Author: AI Assistant
 * * Date Created: 14-Sep-2025
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Paper,
  Tabs,
  Tab,
  Fade,
  Skeleton,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { postRecord } from "services/services";

const API_GetAllFaqs = "faqs/getall";

export default function FaqView(props) {
  const { sectionSettings } = props;
  const theme = useTheme();
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  // Get headings from sectionSettings or use defaults
  const title = sectionSettings?.customTitle || "Frequently Asked Questions";
  const subHeading =
    sectionSettings?.customSubHeading ||
    "Find answers to common questions about our products and services";
  const subText = sectionSettings?.customSubText || "";

  // Fetch FAQs on component mount
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await postRecord(API_GetAllFaqs, {});
        if (response.status === 200 && response.data) {
          // Filter only active FAQs
          const activeFaqs = response.data.filter(
            (faq) => faq.Is_Active === "1"
          );
          setFaqs(activeFaqs);
          setFilteredFaqs(activeFaqs);

          // Extract unique categories
          const uniqueCategories = [
            ...new Set(activeFaqs.map((faq) => faq.Category)),
          ]
            .filter((category) => category && category.trim())
            .sort();
          setCategories(uniqueCategories);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  // Filter FAQs based on category and search term
  useEffect(() => {
    let filtered = faqs;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((faq) => faq.Category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (faq) =>
          faq.Question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          faq.Answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
          faq.Category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredFaqs(filtered);
  }, [faqs, selectedCategory, searchTerm]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setExpandedAccordion(false); // Close all accordions when changing category
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setExpandedAccordion(false); // Close all accordions when searching
  };

  // Group FAQs by category for display
  const groupedFaqs = filteredFaqs.reduce((groups, faq) => {
    const category = faq.Category || "General";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(faq);
    return groups;
  }, {});

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="30%" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 4 }} />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={56}
            sx={{ borderRadius: "28px", mb: 3 }}
          />
          <Skeleton
            variant="rectangular"
            width="200px"
            height={40}
            sx={{ borderRadius: "20px" }}
          />
        </Box>

        {[1, 2, 3, 4, 5].map((item) => (
          <Box key={item} sx={{ mb: 2 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={64}
              sx={{
                borderRadius: "8px",
                mb: 1,
              }}
            />
          </Box>
        ))}
      </Container>
    );
  }

  if (faqs.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: "center",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.08
            )}, ${alpha(theme.palette.primary.main, 0.04)})`,
            backdropFilter: "blur(20px)",
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <QuestionAnswerIcon
            sx={{ fontSize: 64, color: "primary.main", mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            No FAQs Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Frequently asked questions will appear here once they are added.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        {sectionSettings?.showTitle !== false && (
          <>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
              }}
            >
              {title}
            </Typography>
            {subHeading && (
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: "auto" }}
              >
                {subHeading}
              </Typography>
            )}
            {subText && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: "auto", mt: 1 }}
              >
                {subText}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 0,
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.1
          )}, ${alpha(theme.palette.background.paper, 0.1)})`,
          backdropFilter: "blur(20px)",
          //   border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          //   borderRadius: 3,
          //   boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <TextField
          variant="standard"
          fullWidth
          placeholder="Search FAQs..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              backdropFilter: "blur(10px)",
              borderRadius: 2,
            },
          }}
        />
      </Paper>

      {/* Category Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.8
          )}, ${alpha(theme.palette.background.paper, 0.5)})`,
          backdropFilter: "blur(20px)",
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Tabs
          value={selectedCategory}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: alpha(theme.palette.divider, 0.3),
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 56,
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                All Categories
                <Chip
                  label={faqs.length}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            }
            value="all"
          />
          {categories.map((category) => (
            <Tab
              key={category}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {category}
                  <Chip
                    label={
                      faqs.filter((faq) => faq.Category === category).length
                    }
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              }
              value={category}
            />
          ))}
        </Tabs>
      </Paper>

      {/* FAQ Accordions */}
      <Fade in={true} timeout={500}>
        <Box>
          {filteredFaqs.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.warning.main,
                  0.1
                )}, ${alpha(theme.palette.warning.main, 0.05)})`,
                backdropFilter: "blur(20px)",
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
                borderRadius: 3,
                boxShadow: `0 8px 32px ${alpha(
                  theme.palette.warning.main,
                  0.1
                )}`,
              }}
            >
              <SearchIcon sx={{ fontSize: 48, color: "warning.main", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No FAQs Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or selecting a different category.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
                <Grid item xs={12} key={category}>
                  {selectedCategory === "all" && (
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 1.5,
                        mt: 2,
                        fontWeight: 600,
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {category}
                    </Typography>
                  )}
                  {categoryFaqs.map((faq, index) => (
                    <Accordion
                      key={faq.FAQ_Id}
                      expanded={expandedAccordion === `${category}-${index}`}
                      onChange={handleAccordionChange(`${category}-${index}`)}
                      elevation={0}
                      sx={{
                        mb: 1,
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.background.paper,
                          0.8
                        )}, ${alpha(theme.palette.background.paper, 0.5)})`,
                        backdropFilter: "blur(20px)",
                        border: `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.15
                        )}`,
                        borderRadius: 2.5,
                        "&:before": { display: "none" },
                        "&.Mui-expanded": {
                          boxShadow: `0 12px 40px ${alpha(
                            theme.palette.primary.main,
                            0.15
                          )}`,
                          border: `1px solid ${alpha(
                            theme.palette.primary.main,
                            0.3
                          )}`,
                          transform: "translateY(-2px)",
                          transition: "all 0.3s ease-in-out",
                        },
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: `0 8px 32px ${alpha(
                            theme.palette.primary.main,
                            0.12
                          )}`,
                          transition: "all 0.3s ease-in-out",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{
                              color: "primary.main",
                              transition: "transform 0.2s",
                            }}
                          />
                        }
                        sx={{
                          py: 1.5,
                          "&.Mui-expanded": {
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                          },
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            width: "100%",
                          }}
                        >
                          <QuestionAnswerIcon
                            sx={{ color: "primary.main", flexShrink: 0 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              flex: 1,
                              color: "text.primary",
                            }}
                          >
                            {faq.Question}
                          </Typography>
                          {selectedCategory === "all" && (
                            <Chip
                              label={faq.Category}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ flexShrink: 0 }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          pt: 0,
                          pb: 2,
                          px: 3,
                          bgcolor: alpha(theme.palette.background.paper, 0.3),
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.7,
                            color: "text.secondary",
                            pl: 5, // Indent to align with icon
                          }}
                        >
                          {faq.Answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Fade>

      {/* Footer Stats */}
      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 2.5,
          textAlign: "center",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )}, ${alpha(theme.palette.primary.main, 0.04)})`,
          backdropFilter: "blur(20px)",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {filteredFaqs.length} of {faqs.length} frequently asked
          questions
          {selectedCategory !== "all" && ` in "${selectedCategory}" category`}
        </Typography>
      </Paper>
    </Container>
  );
}
